/**
 * The canonical member-facing wording for DrCare's ASYNCHRONOUS consultation.
 *
 * Oval's DrCare intake used to end at `/calendar`, and every surface told the member
 * to book and attend a physician phone call. The contract is now asynchronous: the
 * member submits an intake, a licensed physician reviews it, and an approval or denial
 * arrives as a status update. There is no routine call to attend.
 *
 * ⚠️ THIS MODULE IS THE ONE PLACE THAT WORDING LIVES. Four surfaces render it
 * (checkout, the post-checkout order status screen, the dashboard/notifications, and
 * the conditional scheduling pages), and before this module existed the same sentence
 * was retyped on each of them and drifted. Import the constants; ⛔ do not retype them.
 *
 * ⚠️ `api/` CANNOT import this file — the API and the web bundle are separate module
 * graphs, so `api/services/external/emailService.js` carries a hand-kept COPY of the
 * same sentences. If you edit the wording here, edit it there too; the pairing is
 * recorded in `docs/oval/CODEBASE.md` so the two cannot silently fork.
 *
 * Scheduling is now GONE, not conditional (card 49666). Every consultation is
 * asynchronous and no appointment is required, so `shouldOfferScheduling` — the single
 * predicate every surface asks — answers `false` unconditionally. The two cases it used
 * to admit (a product whose `doctor_now_config.requires_appointment` is `true`, and a
 * refill order whose `refill.scheduling_required` is `true`) are deliberately no longer
 * honoured. The catalogue rows and the refill flag are left UNTOUCHED, so this is a
 * one-commit revert rather than a data migration.
 *
 * ROLLBACK: restore the three predicate bodies below to their dual-case `=== true` reads.
 */

/** A licensed physician reviews the submitted intake; no appointment is attended. */
const ASYNC_REVIEW_NOTICE =
  'A licensed physician reviews the intake you submitted and makes a decision online.';

/** What the member should watch for instead of a calendar invitation. */
const APPROVAL_UPDATE_NOTICE =
  'Watch for an approval or denial update — we will email you and show it in your dashboard.';

/** Contact is the exception, not the expected next step. */
const CONTACT_IF_QUESTIONS_NOTICE =
  'The doctor will reach out only if they need to ask you additional questions.';

/** Said plainly, because the old flow trained members to expect a booking step. */
const NO_APPOINTMENT_NEEDED_NOTICE =
  'No appointment is needed for this request.';

/**
 * Card 49666: no product requires a booked appointment any more.
 *
 * Kept as a function rather than deleted because four surfaces import it; returning a
 * constant disables every one of them at once and keeps the revert to one commit. It
 * ignores its argument on purpose — reading `doctor_now_config.requires_appointment`
 * again is exactly how a catalogue row would resurrect a booking step.
 */
const requiresAppointmentProduct = () => false;

/**
 * Card 49666: a refill is never scheduled any more.
 *
 * `api/controllers/refillController.js` no longer publishes `schedulingRequired: true`,
 * so the flag this used to read can no longer arrive set; answering a constant keeps the
 * web and API halves from disagreeing while that column still exists in the database.
 */
const requiresSchedulingRefill = () => false;

/**
 * May a surface show a scheduling affordance? Card 49666: never.
 *
 * Still the SINGLE question every surface asks, so the pages stay thin renderers and a
 * future reinstatement has exactly one place to change.
 */
const shouldOfferScheduling = () => false;

export {
  ASYNC_REVIEW_NOTICE,
  APPROVAL_UPDATE_NOTICE,
  CONTACT_IF_QUESTIONS_NOTICE,
  NO_APPOINTMENT_NEEDED_NOTICE,
  requiresAppointmentProduct,
  requiresSchedulingRefill,
  shouldOfferScheduling
};
